import Hls from 'hls.js';
import { ChangeDetectorRef, Component, ElementRef, HostListener, NgZone, ViewChild, AfterViewInit, OnDestroy, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppService } from 'src/app/core/services/app.service';
import { CommanSubscribeService } from 'src/app/core/services/comman-subscribe.service';
import { ReportComponent } from '../../general/report/report.component';
import { CONST_AWS_API, API } from 'src/app/core/constants';
import { API as AWS_API } from 'aws-amplify';
import { environment } from 'src/environments/environment';
import { ConsumerSignInComponent } from '../../landing-pages/consumer/consumer-sign-in/consumer-sign-in.component';
import { StorageService } from 'src/app/core/services/storage.service';
import { PubNubService } from 'src/app/core/services/pubnub.service';
import { v4 as uuidv4 } from 'uuid';
import { ToastrService } from 'ngx-toastr';
import { COLORLIST } from 'src/app/core/constants';
import { fa0 } from '@fortawesome/free-solid-svg-icons';
import { STORMCLOUD_SCRIPT_URL } from './stormcloud-script';

declare global {
  interface Window {
    StormcloudVP: {
      StormcloudVideoPlayer: new (config: StormcloudVideoPlayerConfig) => StormcloudVideoPlayer;
    };
  }
}

interface StormcloudVideoPlayerConfig {
  videoElement: HTMLVideoElement;
  src: string;
  autoplay?: boolean;
  muted?: boolean;
  controls?: boolean;
  allowNativeHls?: boolean;
  showCustomControls?: boolean;
  licenseKey?: string;
  immediateManifestAds?: boolean;
  debugAdTiming?: boolean;
  [key: string]: any;
}

interface StormcloudVideoPlayer {
  load(): void;
  destroy?(): void;
  [key: string]: any;
}
@Component({
  selector: 'app-video-player-page',
  templateUrl: './video-player-page.component.html',
  styleUrls: ['./video-player-page.component.scss']
})
export class VideoPlayerPageComponent implements AfterViewInit, OnDestroy {
  subscribeStreamInfo:Subscription;
  selectedStreamInfo:any;
  videoURL: string = '';
  vmapUrl: string = '';
  selectedVideo:any;
  userInfo: any = {};
  userInfoSubscription: Subscription;
  currentVideo:any;
  settingsData: any = {};
  settingsDataSubscription:Subscription
  startSessionResponse:any;
  tag_category:any;
  currentVideoTime =0;
  channelName:any;
  showAd:any;
  comments: any[] = [];
  newComment = '';
  inputFocused = false;
  showEmojiPicker = false;
  subEvents:any;
  channelURL:any;
  userColor: any = {};
  countUserColor = 0;
  isCheckingChannel:boolean=false;
  isChatEnabled:boolean=false;
  isCommentsEnable:boolean=false;
  isSocialShareVisible: boolean = false;
  @ViewChild('commentInput') commentInput!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('videoElement') videoElement!: ElementRef<HTMLVideoElement>;
  protected player: any;
  protected hlsInstance: Hls | null = null;
  /** The album-video page reads the saved stream info; the inline player on the detail page gets it as an input. */
  protected listensToSavedStreamInfo = true;
  private checkVideoElementInterval: any;

    @Input() videoSrc: string = '';
    @Input() autoplay: boolean = false;
    @Input() muted: boolean = false;
    @Input() controls: boolean = true;
    @Input() allowNativeHls: boolean = false;
    @Input() showCustomControls: boolean = true;
    @Input() licenseKey: string = 'bigbee112';
    @Input() immediateManifestAds: boolean = false;
    @Input() debugAdTiming: boolean = true;

  constructor(private commonSubscribeService: CommanSubscribeService,
    private activatedRoute: ActivatedRoute,
    private appService: AppService,
    private dialog: MatDialog,
    public router:Router,
    private storageService: StorageService,
    private pubnubService:PubNubService,
    private toastr:ToastrService,
    private cdr:ChangeDetectorRef,
    private zone: NgZone
  ) { 
    this.appService.currentPlayingVideo.subscribe(
      (video) => {
        this.currentVideo = video;
      }
    )
    this.appService.currentPlayingVideoTime.subscribe(
      (time) => {
        this.currentVideoTime = time;
      }
    )
  }

  ngAfterViewInit(): void {
    console.log('ngAfterViewInit - Initializing video player');
    this.initializeVideoPlayer();
  }

  private initializeVideoPlayer(): void {
    console.log('Initializing video player...');
    
    // First, ensure the video element is in the DOM
    if (!this.videoElement?.nativeElement) {
      console.warn('Video element not found, will retry...');
      this.retryVideoElementInitialization();
      return;
    }

    // If we have a video URL, try to initialize the player
    if (this.videoURL) {
      if (window.StormcloudVP?.StormcloudVideoPlayer) {
        this.initializeStormcloudPlayer();
      } else {
        this.loadStormcloudScript();
      }
    } else {
      console.warn('Video URL not set yet, waiting for stream info...');
      // The URL will be set by the subscription, which will then call initializeVideoPlayer
    }
  }

  private async ensureVideoElementExists(maxRetries = 5, interval = 200): Promise<boolean> {
    return new Promise((resolve) => {
      let attempts = 0;
      
      const checkElement = () => {
        attempts++;
        const videoEl = this.videoElement?.nativeElement;
        
        if (videoEl && document.body.contains(videoEl)) {
          console.log('Video element found in DOM');
          resolve(true);
          return;
        }
        
        if (attempts >= maxRetries) {
          console.error('Video element not found in DOM after maximum retries');
          resolve(false);
          return;
        }
        
        console.log(`Video element not found, retrying... (${attempts}/${maxRetries})`);
        setTimeout(checkElement, interval);
      };
      
      checkElement();
    });
  }

  private async initializeVideoPlayerWithRetry() {
    try {
      const elementExists = await this.ensureVideoElementExists();
      
      if (!elementExists) {
        console.error('Video element not found in DOM after retries');
        this.fallbackToNativePlayer();
        return;
      }

      if (!this.videoURL) {
        console.warn('Video URL not set, checking video element for source...');
        const src = this.videoElement.nativeElement.getAttribute('src') || 
                   this.videoElement.nativeElement.querySelector('source')?.src;
        
        if (src) {
          this.videoURL = src;
          console.log('Using video source from element:', this.videoURL);
        } else {
          throw new Error('No video source available');
        }
      }

      if (window.StormcloudVP?.StormcloudVideoPlayer) {
        this.initializeStormcloudPlayer();
      } else {
        console.log('Stormcloud Video Player not loaded yet, loading script...');
        this.loadStormcloudScript();
      }
    } catch (error) {
      console.error('Error initializing video player:', error);
      this.fallbackToNativePlayer();
    }
  }

  private retryVideoElementInitialization(): void {
    // Clear any existing interval
    if (this.checkVideoElementInterval) {
      clearInterval(this.checkVideoElementInterval);
    }

    // Try to find the video element every 100ms for 5 seconds
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds total (50 * 100ms)
    
    this.checkVideoElementInterval = setInterval(() => {
      attempts++;
      
      // Try to find the video element by ID as a fallback
      const videoEl = document.querySelector('video');
      if (videoEl) {
        console.log('Found video element in DOM, initializing player...');
        this.videoElement = { nativeElement: videoEl } as ElementRef<HTMLVideoElement>;
        clearInterval(this.checkVideoElementInterval);
        this.initializeStormcloudPlayer();
        return;
      }

      if (attempts >= maxAttempts) {
        console.error('Failed to find video element after multiple attempts');
        clearInterval(this.checkVideoElementInterval);
        this.fallbackToNativePlayer();
      }
    }, 100);
  }

  private loadStormcloudScript(): void {
    if (window.StormcloudVP?.StormcloudVideoPlayer) {
      this.initializeStormcloudPlayer();
      return;
    }

    // Check if script is already being loaded
    if (document.querySelector('script[src*="stormcloud"]')) {
      const checkScript = setInterval(() => {
        if (window.StormcloudVP?.StormcloudVideoPlayer) {
          clearInterval(checkScript);
          this.initializeStormcloudPlayer();
        }
      }, 100);
      return;
    }

    // Check if video URL is set
    if (!this.videoURL) {
      console.warn('Video URL not set, checking for video source...');
      // Try to get URL from video element
      const src = this.videoElement?.nativeElement?.getAttribute('src');
      if (src) {
        this.videoURL = src;
      } else {
        console.error('No video source available');
        this.fallbackToNativePlayer();
        return;
      }
    }

    // Load the script
    const script = document.createElement('script');
    script.src = STORMCLOUD_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      if (window.StormcloudVP?.StormcloudVideoPlayer) {
        this.initializeStormcloudPlayer();
      } else {
        console.error('Stormcloud Video Player script loaded but not properly initialized');
        this.fallbackToNativePlayer();
      }
    };
    script.onerror = () => {
      console.error('Failed to load Stormcloud Video Player script');
      this.fallbackToNativePlayer();
    };
    document.head.appendChild(script);
  }

  // Handle video metadata loaded event
  onVideoLoaded(event: Event): void {
    console.log('Video metadata loaded', event);
    const video = event.target as HTMLVideoElement;
    console.log('Video dimensions:', video.videoWidth, 'x', video.videoHeight);
    console.log('Video duration:', video.duration);
  }

  // Handle video errors
  onVideoError(event: Event): void {
    const video = event.target as HTMLVideoElement;
    console.error('Video error:', video.error);
    console.error('Video network state:', video.networkState);
    console.error('Video ready state:', video.readyState);
    
    // Try to recover or provide user feedback
    if (video.error) {
      switch (video.error.code) {
        case MediaError.MEDIA_ERR_ABORTED:
          console.error('You aborted the video playback');
          break;
        case MediaError.MEDIA_ERR_NETWORK:
          console.error('A network error caused the video download to fail');
          break;
        case MediaError.MEDIA_ERR_DECODE:
          console.error('The video playback was aborted due to a corruption problem');
          break;
        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
          console.error('The video could not be loaded, either because the server or network failed or because the format is not supported');
          break;
        default:
          console.error('An unknown error occurred');
      }
    }
    
    // Fallback to native player
    this.fallbackToNativePlayer();
  }

  private fallbackToNativePlayer(): void {
    console.log('Falling back to native video player');
    
    if (!this.videoElement?.nativeElement) {
      console.error('Cannot fallback: Video element not found');
      return;
    }

    const videoEl = this.videoElement.nativeElement;
    const src = this.videoURL || videoEl.querySelector('source')?.src || '';

    if (!src) {
      console.error('Cannot fallback: No video source available');
      return;
    }

    if (Hls.isSupported()) {
      if (this.hlsInstance) {
        this.hlsInstance.destroy();
      }
      this.hlsInstance = new Hls({ xhrSetup: (xhr) => { xhr.withCredentials = false; } });
      this.hlsInstance.loadSource(src);
      this.hlsInstance.attachMedia(videoEl);
      this.hlsInstance.on(Hls.Events.MANIFEST_PARSED, () => {
        videoEl.play().catch(e => console.warn('Autoplay failed:', e));
      });
    } else if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari supports HLS natively
      videoEl.src = src;
      videoEl.load();
      videoEl.play().catch(e => console.warn('Autoplay failed:', e));
    } else {
      console.error('HLS is not supported in this browser');
    }
  }

  private initializeStormcloudPlayer(): void {
    try {
      if (!window.StormcloudVP?.StormcloudVideoPlayer) {
        throw new Error('Stormcloud Video Player is not available');
      }

      if (!this.videoElement?.nativeElement) {
        throw new Error('Video element not found in DOM');
      }

      if (!this.videoURL) {
        throw new Error('Video URL is not set');
      }

      const { StormcloudVideoPlayer } = window.StormcloudVP;
      const videoEl = this.videoElement.nativeElement;

      // Clean up previous player instance if exists
      if (this.player) {
        try {
          if (typeof this.player.destroy === 'function') {
            this.player.destroy();
          }
        } catch (e) {
          console.warn('Error destroying previous player instance:', e);
        }
        this.player = null;
      }

      // Configure video element
      // videoEl.controls = false; // Use Stormcloud's controls
      videoEl.preload = 'auto';

      // Initialize the player
      const playerConfig = {
        videoElement: videoEl,
        src: this.videoURL,
        autoplay: true,
        muted: false,
        controls: true,
        allowNativeHls: false,
        addPlayerType: 'ima',
        licenseKey: this.licenseKey,
        vmapUrl: this.vmapUrl,
        debugAdTiming: true,
      };
      console.log('[StormPlayer] Player settings:', { ...playerConfig, videoElement: '[HTMLVideoElement]' });

      this.player = new StormcloudVideoPlayer(playerConfig);
      this.player.load();
      console.log('[StormPlayer] Player initialized successfully');
    } catch (error) {
      console.error('Error initializing Stormcloud Video Player:', error);
      this.fallbackToNativePlayer();
    }
  }

  ngOnInit(): void {
    const slug = this.activatedRoute.snapshot.params['slug_name'];
    const channel = this.activatedRoute.snapshot.params['channelName'];
    
    if (!slug || !channel) {
      console.error('Missing required route parameters');
      return;
    }

    this.pubnubService.init();
    this.userInfo = this.commonSubscribeService.userInfo;
    this.userInfoSubscription = this.commonSubscribeService.getUserInfo().subscribe(response => {
      this.userInfo = response;
    });
    if (this.listensToSavedStreamInfo) this.subscribeStreamInfo = this.commonSubscribeService.getVideoStreamInfo().subscribe({
      next: async (response) => {
        console.log("Video Player Page Component Stream Info:", response);
        
        if (!response) {
          console.error('No response received from getVideoStreamInfo');
          this.router.navigate([this.commonSubscribeService.getChannelBasePath(channel) + `/album/video/${slug}`]);
          return;
        }

        await this.applyStreamInfo(response);
      },
      error: (error) => {
        console.error('Error getting video stream info:', error);
        this.fallbackToNativePlayer();
      }
    });

    this.settingsData = this.commonSubscribeService.icWebpageSettingsData;
    this.settingsDataSubscription = this.commonSubscribeService.getICWebpageThemeSettings().subscribe(response => {
      this.settingsData = response;
    })
    this.getUserPlaylist();
  }

  /** Loads one video into the player: URL, VMAP ads, Stormcloud / hls player and comments. */
  async applyStreamInfo(response: any): Promise<void> {
    // Update properties from response
    this.selectedVideo = response.selectedVideo;
    this.startSessionResponse = response.startSessionResponse;
    this.selectedStreamInfo = response.selectedStreamInfo;
    this.tag_category = response.tag_category;
    this.channelName = response.channelName;
    this.subEvents = response.subEvents;

    // Set video URL with fallbacks
    // this.videoURL = "https://hls.traffiq.bigbee.ai/live/jIGBc/Test_naag_sports_live_2/testnaaglivesportsv3.m3u8";
    this.videoURL = response.videoURL || response.selectedStreamInfo?.video_url ;
    console.log('Video URL set to:', this.videoURL);

    const vmapBase = response.selectedStreamInfo?.vmap_url || response.selectedVideo?.vmap_url || '';
    const shortId = response.selectedStreamInfo?.showfer_short_id || response.selectedVideo?.showfer_short_id || '';
    this.vmapUrl = vmapBase && shortId ? `${vmapBase}?channel=${shortId}` : '';
    // Initialize the player with retry logic
    await this.initializeVideoPlayerWithRetry();

    // Handle comments if needed
    const streamId = this.subEvents?.[0]?.stream_info?.id;
    if (streamId) {
      this.channelURL = `${streamId}_video_comments`;
      this.isCommentsEnable = this.selectedVideo?.enable_comments === 1 && this.selectedVideo?.publish === 1;
      this.getComments();
      this.subscribeToComments();
    }
  }

  getCurrentTime(e: Event): void {
    const target = e.target as HTMLVideoElement;
    if (target && target.currentTime) {
      this.appService.currentPlayingVideoTime.next(Math.floor(target.currentTime));
    }
  }

  reportFileData(data: any, type: string): void {
    const userId = this.userInfo?.user_first_name + ' ' + this.userInfo?.user_last_name;
    const albumId = this.commonSubscribeService.extractIdFromUrl(this.router.url);
    (window as any).umami?.track(`Album ${albumId} report clicked - ${userId}`);
    const u_payload = {
      actionType: "network",
      metaData: {
        title: `Album ${albumId} report clicked`,
        albumId: albumId,
        videoId: data?.streamid || null,
        videoTitle: data?.stream_info?.stream_video_title || null,
        timeStamp: new Date().toISOString()
      },
      organizationId: this.commonSubscribeService?.slugInfo?.organization_id || null,

      clickAction: `album-report`
    }
    this.commonSubscribeService.sendTriggeredEvent(u_payload);
    if (this.userInfo && this.userInfo.access_token) {
      if (type == 'uploadedVideo' || type == 'recordedVideo' || type == 'playerVideo') {
        if (this.currentVideo && this.currentVideo.streamid == data.streamid) {
          this.appService.reportData.next({ data: data, type: type, time: this.currentVideoTime })
        }
        else {
          this.appService.reportData.next({ data: data, type: type, time: 0 })
        }
      }
      const dialogRef = this.dialog.open(ReportComponent, {
        panelClass: 'app-dialog',
        disableClose: true,
        data: this.settingsData
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result == true) {
        }

      });
    } else {
      this.requireLogin();
    }
  }

  close() {
    if (this.router.url.includes("?id")) {
      this.router.navigate([this.commonSubscribeService.getChannelBasePath(this.channelName) + "/album/" + this.tag_category + "/" + this.selectedStreamInfo?.slug]);

    }
    this.endSessionWithStreamMetrics();
  }

  endSessionWithStreamMetrics() {
    let url = CONST_AWS_API.END_SESSION;
    let id = this.startSessionResponse ? this.startSessionResponse['Data'] : 0;
    let session_start_time = this.startSessionResponse ? this.startSessionResponse['session_start_time'] : "";
    let reqData: any = {};
    if (this.tag_category == 'video' || this.tag_category == 'recorded-video') {
      reqData = this.selectedVideo;
    } 
    let data = {
      headers: { 'x-api-key': environment.AWS_Cognito.X_Api_Key },
      response: true,
      body: {
        "id": id,
        "session_start_time": session_start_time,
        "is_final": true,
        "event_id": reqData.id,
        "image_for": 'stream_metrics/' + reqData.stream_video_code + "/" + id,
        "stream_type": 'video'
      }
    }
    AWS_API.post(environment.AWS_Cognito.apiName, url, data).then(response => {
    }, error => {
      console.log("error");
    });
  }

  requireLogin(data?) {
    let tagCat = this.tag_category ? this.tag_category : data.type;
    let url = '';
    let params = null;
    if (data && data.stream_info && data.stream_info.id) {
      params = Object.keys(this.activatedRoute.snapshot.queryParams).length > 1 ? this.activatedRoute.snapshot.queryParams : { id: data.stream_info.id };
    } else if (data && data.id) {
      params = Object.keys(this.activatedRoute.snapshot.queryParams).length > 1 ? this.activatedRoute.snapshot.queryParams : { id: data.id };
    }
    if (data) {
      let params = null;

      if (data && data.stream_info && data.stream_info.id) {

        params = Object.keys(this.activatedRoute.snapshot.queryParams).length > 1 ? this.activatedRoute.snapshot.queryParams : { id: data.stream_info.id };
        url = this.router.url + '?id=' + data.stream_info.id;
      } else if (data && data.id) {

        params = Object.keys(this.activatedRoute.snapshot.queryParams).length > 1 ? this.activatedRoute.snapshot.queryParams : { id: data.id };

        url = this.router.url + '?id=' + data.id;
      }
      this.router.navigate([this.commonSubscribeService.getChannelBasePath(this.channelName) + "/album/" + tagCat + "/" + data.slug], { queryParams: params });
    }
    url = url ? url : this.router.url;
    this.storageService.setItem('skipIcRedirection', url);
    const dialogRef = this.dialog.open(ConsumerSignInComponent, {
      maxWidth: "400px",
      panelClass: Object.keys(this.settingsData).length > 0 && this.settingsData.form_bg_color ? "app-consumer-signin" : "app-consumer-sign-in",
      disableClose: true,
      data: {}
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result == "success") {
        this.userInfo = this.commonSubscribeService.userInfo;
      }
      if (!this.router.url.includes('/sign-up'))
        this.storageService.deleteItem('skipIcRedirection');
    });
  }
  cancelComment() {
    this.newComment = '';
    this.inputFocused = false;
  }

  postComment() {
    if (!this.newComment.trim()) return;
    let messagePayload: any = {
      type: "text",
      message: this.newComment,
      customType: 'COMMENTS',
      userType: "fan",
      messageId: uuidv4(),
      timestamp: Date.now(),
      _sender: {
        isSender: true,
        profileUrl: this.userInfo.profile_pic || '',
        userId: 'fan_' + this.userInfo.id,
        nickname: this.commentName()
      },
      publishAt: 'fan_' + this.userInfo.id,
    };

    if (this.userInfo.id) {
      this.pubnubService.sendMessage(this.channelURL, messagePayload, (status) => {
        if (status.error) {
          this.toastr.error("Error while sending message");
          return;
        }
        // Show it as soon as PubNub accepts it: the list must not depend on the subscription echoing it back
        // (it showed only after a refresh). The echo, if it comes, is skipped by messageId.
        this.zone.run(() => this.addComment(messagePayload));
      })
    }
    this.newComment = '';
    this.inputFocused = false;
    const textarea = this.commentInput.nativeElement;
    textarea.style.height = "32px";  
  }

  /** Name shown with a comment: display name, else first + last name (a missing name showed as "Fan" to partners). */
  commentName(): string {
    const u = this.userInfo || {};
    const full = `${u.user_first_name || ''} ${u.user_last_name || ''}`.trim();
    return (u.user_display_name || '').trim() || full || 'Fan';
  }

  getUserInitials(): string {
    if (!this.userInfo) return '';
    const first = this.userInfo.user_first_name?.charAt(0)?.toUpperCase() || '';
    const last = this.userInfo.user_last_name?.charAt(0)?.toUpperCase() || '';
    return `${first}${last}`;
  }
  connectCommentsChannel() {
    this.isChatEnabled = true;
    this.pubnubService.connect(
      this.userInfo && this.userInfo?.id?.toString(),
      (success, error) => {
        if (error) {
          this.isCheckingChannel = false;
          console.log("Error while connecting chat user", error);
          return;
        } else {
          this.pubnubService.enterChannel(
            this.channelURL,
            1,
            (openChannel, error) => {
              if (error) {
                this.isCheckingChannel = false;
                console.log("Error while connecting chat channel", error);
                return;
              }
              this.isCheckingChannel = true;
              this.getComments();
            }
          );
        }
      }
    );
  }
  getComments(){
    this.pubnubService.getAllMessage(this.channelURL, 100, (messagesList, status) => {
      this.comments = messagesList.map((msg: any) => {
        const entry = msg.entry;
        if (entry) {
          entry._sender.userColor = this.getColor(entry);
          entry._sender.shortName = this.getName(entry._sender.nickname);
        }
        return {
          _sender: entry?._sender,
          message: entry?.message,
          messageId: entry?.messageId,
          timestamp: entry?.timestamp,
          time: new Date(entry?.timestamp)
        };
      }).reverse();
    })
    
  }

  subscribeToComments(){
    this.pubnubService.receiveCommentMessages(this.channelURL, (msg) => {
      this.zone.run(() => {
      const payload = msg.message;

      if (!payload) return;
      this.addComment(payload);
    })
   })
  }
  /** Adds a comment at the top once: the sender's own comment arrives both from send and from the subscription. */
  addComment(payload: any): void {
    if (payload?.messageId && (this.comments || []).some(c => c.messageId === payload.messageId)) return;
    const newComment = {
      _sender: {
        ...payload._sender,
        userColor: this.getColor(payload),
        shortName: this.getName(payload._sender?.nickname || '')
      },
      message: payload?.message || '',
      messageId: payload?.messageId,
      time: new Date(payload?.timestamp),
      timestamp: payload?.timestamp
    };
    this.comments = [newComment, ...(this.comments || [])];
  }

  getColor(message) {
    const userId = message._sender?.userId ?? '';
    if (!userId) return COLORLIST[0];
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      hash = (hash + userId.charCodeAt(i)) % COLORLIST.length;
    }
    return COLORLIST[hash];
  }
  
  getName(name) {
    let returnString = "";
    if (name) {
      let a = name.split(" ");
      if (a.length > 1) {
        returnString = a[0].charAt(0);
        returnString += a[1].charAt(0);
        return returnString;
      } else if (name) {
        returnString = a[0].charAt(0);
        returnString += a[0].charAt(1);
        return returnString;
      } else {
        return returnString;
      }
    }
  }

  //Emoji
  toggleEmojiPicker() {
    this.showEmojiPicker = !this.showEmojiPicker;
  }
  
  addEmoji(event: { emoji?: { native: string }, native?: string }): void {
    const emoji = event?.emoji?.native || event?.native || '';
    this.newComment = (this.newComment || '') + emoji;
    this.showEmojiPicker = false;
  }

  getNameInitials(name: string): string {
    if (!name) return '';
    const parts = name.trim().split(' ');
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    const first = parts[0].charAt(0).toUpperCase();
    const last = parts[1].charAt(0).toUpperCase();
    return `${first}${last}`;
  }
  

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (this.showEmojiPicker && 
        this.commentInput?.nativeElement && 
        !(this.commentInput.nativeElement as HTMLElement).contains(event.target as Node)) {
      this.showEmojiPicker = false;
    }
  }

  likeVideo() {
    if (this.selectedVideo) {
      const data = {
        headers: {
          "Content-Type": "application/json",
          "x-api-key": environment.AWS_Cognito.X_Api_Key,
          "Authorization": `Bearer ${this.userInfo.access_token}`
        },
        response: true,
        body: {
          event_id: this.selectedVideo.parent_streams_id,
          stream_id: this.selectedVideo.id,
          organization_id: this.selectedVideo.organization_id,
          userLike: this.selectedVideo.is_liked === 0 ? 1 : 0
        }
      };

      AWS_API.post(
        environment.AWS_Cognito.apiName,
        CONST_AWS_API.LIKE_UNLIKE_AUDIO_VIDEO,
        data
      )
        .then(response => {
          if (response.data.statusCode === '200') {
            if (this.selectedVideo.is_liked === 1) {
              this.selectedVideo.is_liked = 0;
              this.selectedVideo.user_likes = this.selectedVideo.user_likes -1;
            } else {
              this.selectedVideo.is_liked = 1;
              this.selectedVideo.user_likes = this.selectedVideo.user_likes + 1;
            }
          }
        })
        .catch(error => console.log(error));
    } else {
      console.log('No matching video found for id');
    }

  }
  
  shareVideo() {
  this.isSocialShareVisible = !this.isSocialShareVisible;
  }
    
  closeSocialSharePopup(event: any) {
  this.isSocialShareVisible = false;
  }

  getUserPlaylist() {
    let data = {
      headers: {
        "Content-Type": "application/json",
        "x-api-key": environment.AWS_Cognito.X_Api_Key,
        "Authorization": `Bearer ${this.userInfo.access_token}`
      },
      response: true,
      body: {
        "user_id": Number(this.userInfo.id),
        "organization_id": this.commonSubscribeService?.slugInfo?.stream_info?.organization_id || this.commonSubscribeService?.slugInfo?.organization_id || null
      }
    }
    AWS_API.post(environment.AWS_Cognito.apiName, CONST_AWS_API.GET_USER_PLAYLIST, data).then(response => {
      if (response.data.statusCode === '200') {
        this.getEventBySlug();
      }
    }).catch(error => {

    });

  }

  getEventBySlug(){
    let data = {
      headers: {
        "Content-Type": "application/json",
        "x-api-key": environment.AWS_Cognito.X_Api_Key,
        "Authorization": `Bearer ${this.userInfo.access_token}`,
      },
      response: true,
      body: {
        "slug": this.selectedVideo?.slug,
        "userid": this.userInfo?.id || undefined,
        "offset": new Date().getTimezoneOffset(),
        // "currency": localCurrency ? localCurrency : currentCode,
        // "timezone": this.timezoneVal
      }
    }
    AWS_API.post(environment.AWS_Cognito.apiName, CONST_AWS_API.GET_EVENT_BY_SLUG, data).then(response => {
      if (response.data.statusCode === '200') {
        const subEvents = response?.data?.Data?.subEvents || [];
        for (let se of subEvents) {
          if (se?.stream_info?.id === this.selectedVideo?.id) {
            this.selectedVideo = se.stream_info;
            break;
          }
        }
      }
    })
  }

  onEnterPress(event: KeyboardEvent): void {
    if (event.shiftKey) return;
  
    event.preventDefault(); 
  
    if (this.newComment?.trim()) {
      if (this.userInfo && this.userInfo.access_token) {
        this.postComment();
      } else {
        this.requireLogin();
      }
    }
  }
  
  autoGrowTextarea(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    target.style.height = 'auto';
    target.style.height = target.scrollHeight + 'px';
  }
  

  ngOnDestroy() {
    if (this.userInfoSubscription) {
      this.userInfoSubscription.unsubscribe();
    }
    if (this.subscribeStreamInfo) {
      this.subscribeStreamInfo.unsubscribe();
    }
    if (this.hlsInstance) {
      this.hlsInstance.destroy();
      this.hlsInstance = null;
    }
    if (this.checkVideoElementInterval) {
      clearInterval(this.checkVideoElementInterval);
    }
  }

}
